import { type LimitationView, type Permission, type PermissionView } from "../types";
import { DropdownWrapper } from "../../../../../components/dropdowns";
import ChecklistDropdown from "../../../../../components/dropdowns/checklistDropdown/v2/ChecklistDropdownV2";
import { Grid } from "semantic-ui-react";
import { useMemo, useRef } from "react";
import AreaIcon from "../../../../../components/areaIcon/AreaIcon";
import { cloneDeep, set } from "lodash";
import ObjectUtils from "../../../../../utils/objectUtils";
import { limitationTypes } from "./constants";

interface NamedItem {
  id: number | string;
  name: string;
}

interface DropdownOption {
  key: number | string;
  value: number | string;
  text: string;
  icon?: React.ReactNode;
}

interface LimitationProvider {
  isLoading: () => boolean;
  items: () => LimitationView[];
  onFetch: () => void;
}

export interface PermissionRowProps {
  availablePermissions: Permission[];
  row: PermissionView;
  getDefaultPermission: () => PermissionView;
  initialPermissions?: PermissionView;
  index: number;
  isReadOnly: boolean;
  configuredPermissions: PermissionView[];
  onPermissionsChange: (permissions: PermissionView[]) => void;
  limitationProvider: (id: number) => LimitationProvider;
}

const getAvailableAreasWithIcon = (values: NamedItem[]): DropdownOption[] => {
  return values.map((x) => ({
    key: x.id,
    value: x.id,
    text: x.name,
    icon: <AreaIcon areaName={x.name} />,
  }));
};

const getOptions = (values: NamedItem[]): DropdownOption[] => {
  return values.map((x) => ({
    key: x.id,
    value: x.id,
    text: x.name,
  }));
};

const getAvailableFeatures = (row: PermissionView, availablePermissions: Permission[]): DropdownOption[] => {
  if (!row.area) {
    return [];
  }

  const area = availablePermissions.find((curr) => curr.areaId === row.area);
  if (!area) return [];

  const availableFeatures = area.features.map((fp) => ({
    id: fp.featureId,
    name: fp.featureName,
  }));

  return getOptions(availableFeatures);
};

const isContextAvailable = (row: PermissionView) => {
  const typesWithLimitationItems = [
    limitationTypes.specificAccounts,
    limitationTypes.group,
    limitationTypes.inheritedGroup,
  ];
  return row.limitation !== null && typesWithLimitationItems.includes(row.limitation!);
};

const emptyArr: LimitationView[] = [];

const fieldsOrder: Array<keyof PermissionView> = ["area", "feature", "right", "limitation", "limitationItems"];

const handleValueChange = (
  property: keyof PermissionView,
  value: unknown,
  current: PermissionView,
  defaultObj: PermissionView,
): PermissionView => {
  const copy = cloneDeep(current);

  fieldsOrder.slice(fieldsOrder.indexOf(property) + 1).forEach((field) => {
    set(copy, field, defaultObj[field]);
  });

  copy[property] = value as never;
  return copy;
};

export const PermissionRow = (props: PermissionRowProps): JSX.Element => {
  const {
    row,
    isReadOnly,
    availablePermissions,
    limitationProvider,
    configuredPermissions,
    index,
    initialPermissions,
    getDefaultPermission,
  } = props;
  const { area, limitation, feature, right } = row;
  const limitationNames = useRef<Map<string, string>>(new Map());

  const getLimitationName = (row: PermissionView): string => {
    if (!row.area || !row.feature || !row.right || !row.limitation) {
      throw new Error("Invalid permission row: missing required fields");
    }

    const key = `${row.area}:${row.feature}:${row.right}:${row.limitation}`;

    if (limitationNames.current.has(key)) {
      const name = limitationNames.current.get(key);
      if (!name) {
        throw new Error(`Cached limitation name for key "${key}" is undefined`);
      }
      return name;
    }

    const areaObj = availablePermissions.find((fp) => fp.areaId === row.area);
    if (!areaObj) {
      throw new Error(`Area with ID "${row.area}" not found`);
    }

    const featureObj = areaObj.features.find((fp) => fp.featureId === row.feature);
    if (!featureObj) {
      throw new Error(`Feature with ID "${row.feature}" not found in area "${row.area}"`);
    }

    const rightObj = featureObj.rights.find((r) => r.id === row.right);
    if (!rightObj) {
      throw new Error(`Right with ID "${row.right}" not found in feature "${row.feature}"`);
    }

    const limitationObj = rightObj.limitations.find((lt) => lt.id === row.limitation);
    if (!limitationObj) {
      throw new Error(`Limitation with ID "${row.limitation}" not found in right "${row.right}"`);
    }

    const name = limitationObj.name;
    limitationNames.current.set(key, name);
    return name;
  };

  const availableAreas = useMemo(() => {
    const areas = availablePermissions
      .filter(
        (a) =>
          a.areaId === area ||
          getAvailableFeatures({ area: a.areaId } as PermissionView, availablePermissions).length > 0,
      )
      .map((ap) => ({ id: ap.areaId, name: ap.areaName }));

    return getAvailableAreasWithIcon(areas);
  }, [area, availablePermissions]);

  const availableFeatures = useMemo(() => {
    if (!area) {
      return [];
    }

    const areaObj = availablePermissions.find((curr) => curr.areaId === area);
    if (!areaObj) return [];

    const features = areaObj.features.map((fp) => ({
      id: fp.featureId,
      name: fp.featureName,
    }));

    return getOptions(features);
  }, [area, availablePermissions]);

  const availableRights = useMemo(() => {
    if (!feature || !area) {
      return [];
    }

    const areaObj = availablePermissions.find((fp) => fp.areaId === area);
    if (!areaObj) return [];

    const featureObj = areaObj.features.find((fp) => fp.featureId === feature);
    if (!featureObj) return [];

    const rights = featureObj.rights.map((r) => ({
      id: r.id,
      name: r.name,
    }));

    return getOptions(rights);
  }, [feature, area, availablePermissions]);

  const availableLimitations = useMemo(() => {
    if (!right || !feature || !area) {
      return [];
    }

    const areaObj = availablePermissions.find((fp) => fp.areaId === area);
    if (!areaObj) return [];

    const featureObj = areaObj.features.find((fp) => fp.featureId === feature);
    if (!featureObj) return [];

    const rightObj = featureObj.rights.find((r) => r.id === right);
    if (!rightObj) return [];

    const limitations = rightObj.limitations.map((l) => ({
      id: l.id,
      name: l.name,
    }));

    return getOptions(limitations);
  }, [area, availablePermissions, feature, right]);

  const onChange = (name: keyof PermissionView, value: unknown): void => {
    const changed = name === "limitation" ? onSelectLimitationField(value as number) : onSelectField(name, value);

    props.onPermissionsChange(changed);
  };

  const onSelectField = (name: keyof PermissionView, value: unknown): PermissionView[] => {
    const copy = cloneDeep(configuredPermissions);
    return set(copy, index, handleValueChange(name, value, copy[index], getDefaultPermission()));
  };

  const onSelectLimitationField = (value: number): PermissionView[] => {
    const nextConfiguredPermissions = [...configuredPermissions];

    const updatedItem: PermissionView = {
      ...nextConfiguredPermissions[index],
      limitation: value,
      limitationItems: undefined,
    };

    if (value === limitationTypes.none) {
      nextConfiguredPermissions
        .filter(
          (p) =>
            p.area === updatedItem.area &&
            p.feature === updatedItem.feature &&
            p.right === updatedItem.right &&
            !p.limitation,
        )
        .forEach((p) => {
          let i = nextConfiguredPermissions.indexOf(p);
          nextConfiguredPermissions[i] = {
            ...nextConfiguredPermissions[i],
            right: undefined,
          };
        });
    }

    if (isContextAvailable(updatedItem)) {
      updatedItem.limitationItems = [] as PermissionView["limitationItems"];
      limitationProvider(value).onFetch();
    }

    nextConfiguredPermissions[index] = updatedItem;

    return nextConfiguredPermissions;
  };

  const isLimitationsAvailable = isContextAvailable(row);

  const initialLimitations = useMemo(() => {
    if (!isLimitationsAvailable) {
      return emptyArr;
    }

    if (ObjectUtils.isEqualBy(initialPermissions, row, "area", "feature", "right", "limitation")) {
      return initialPermissions?.limitationItems || emptyArr;
    }

    return emptyArr;
  }, [isLimitationsAvailable, initialPermissions, row]);

  return (
    <>
      <Grid.Column>
        <DropdownWrapper
          blur
          placeholder="Select Area"
          fluid
          selection
          selected={area}
          handleOptionChange={(_, data) => onChange("area", data.value)}
          items={availableAreas}
          disabled={isReadOnly}
        />
      </Grid.Column>
      <Grid.Column>
        <DropdownWrapper
          blur
          placeholder="Select Feature"
          fluid
          selection
          selected={feature}
          handleOptionChange={(_, data) => onChange("feature", data.value)}
          items={availableFeatures}
          disabled={isReadOnly || !row.area}
        />
      </Grid.Column>
      <Grid.Column>
        <DropdownWrapper
          blur
          placeholder="Select Rights"
          fluid
          selection
          selected={right}
          handleOptionChange={(_, data) => onChange("right", data.value)}
          items={availableRights}
          disabled={isReadOnly || !row.feature}
        />
      </Grid.Column>
      <Grid.Column>
        <DropdownWrapper
          blur
          placeholder="Select Type"
          fluid
          selection
          selected={limitation}
          handleOptionChange={(_, data) => onChange("limitation", data.value)}
          items={availableLimitations}
          disabled={isReadOnly || !right}
        />
      </Grid.Column>
      <Grid.Column>
        {isContextAvailable(row) && limitation !== null && limitation !== undefined && (
          <ChecklistDropdown
            placeholder={`Select ${getLimitationName(row)}`}
            items={limitationProvider(limitation).items()}
            isLoading={limitationProvider(limitation).isLoading()}
            onCheckedItemsChanged={(items) => onChange("limitationItems", items)}
            initialCheckedItems={initialLimitations}
            isReadOnly={isReadOnly}
            sortPredicate={(item: LimitationView) => item.name.toLowerCase()}
            searchPredicate={(item: LimitationView, term: string) =>
              item.name.toLowerCase().includes(term.toLowerCase())
            }
            getLabel={(item: LimitationView) => item.name}
          />
        )}
      </Grid.Column>
    </>
  );
};
